import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
    Search, 
    Filter, 
    Banknote, 
    ChevronDown, 
    ChevronUp, 
    Calendar, 
    Building2, 
    AlertCircle, 
    CheckCircle2, 
    XCircle, 
    Loader2, 
    Trash2,
    Plus,
    ChevronRight,
    DollarSign,
    Send,
    AlertTriangle,
    Link2,
    ExternalLink,
    FileText,
} from 'lucide-react';
import ParcelasParaEmitir from './components/ParcelasParaEmitir';
import EtapaBoletoBadge from './components/EtapaBoletoBadge';
import { useFaturaDaParcela } from './components/useFaturaDaParcela';
import { ACAO_ETAPA, diasEntre, etapaBoletoParcela, type EtapaBoleto } from '../../supabase/functions/_shared/etapaBoletoParcela.ts';
import { DIAS_ANTES_EMITIR } from '../../supabase/functions/_shared/parcelasParaEmitir.ts';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import CriarParcelamentoModal from './CriarParcelamentoModal';
import ConferenciaAcordosView from './ConferenciaAcordosView';
import FaturaFAB from './FaturaFAB';
import { aprovarAcordo, buscarCadastroDevedor, cancelarAcordo, efetivarAcordoManual, enviarTermoAcordo, descreverCanal, podeCancelarAcordo } from '../../src/lib/parcelamentoAcordo';
import { devedorPadrao, emailValido, type DadosTermo } from '../../src/lib/termoNegociacao.ts';
import { avisosDoAcordo, mensagemEnvioEntrada, type AvisoAcordo } from '../../src/lib/entradaAcordoRegras.ts';
import { emitirEEnviarEntrada } from '../../src/lib/entradaAcordo';
import { ESTADOS_PENDENTES, ROTULO_STATUS, type StatusAcordo } from '../../src/lib/parcelamentoRegras.ts';
import { DIAS_PARA_SAIR_SEPARADA } from '../../supabase/functions/_shared/parcelaNaFaturaVigor.ts';
import AcordoTrilhaPanel from './components/AcordoTrilhaPanel';
import ConfirmModal from '../../src/components/ConfirmModal';

interface Acordo {
    id: string;
    unidade_consumidora_id: number | null;
    uc_codigo_snapshot: string | null;
    cliente_nome_snapshot: string | null;
    valor_total: number;
    numero_parcelas: number;
    descricao: string | null;
    status: StatusAcordo;
    created_at: string;
    // Campos do acordo com juros/entrada/termo (chamado #5686). Opcionais porque
    // os 45 acordos anteriores à feature não os têm.
    divida_bruta?: number | null;
    /** Desconto sobre a dívida (migration 20260930170005). */
    desconto_divida?: number | null;
    /** 'gestao' | 'valor_ja_pago' (#6174). Null nos acordos anteriores. */
    desconto_origem?: string | null;
    desconto_descricao?: string | null;
    /** Resultado do motor gravado na criação. Daqui saem os outros débitos
     *  (08/10/2026), cada um com a descrição. */
    memoria_calculo?: { detalheOutrosDebitos?: { descricao: string; valorCentavos: number }[] } | null;
    entrada_valor?: number | null;
    entrada_vencimento?: string | null;
    entrada_fatura_id?: number | null;
    /** Boleto da entrada como está hoje — carregado à parte em fetchAcordos. */
    entrada_fatura?: { num_cobranca_boleto?: string | null; status_boleto_vigor?: string | null; vencimento_boleto?: string | null } | null;
    entrada_envio?: any;
    termo_envio_erro?: string | null;
    termo_entrega?: any;
    taxa_juros_mensal?: number | null;
    juros_total?: number | null;
    total_acordo?: number | null;
    termo_url?: string | null;
    termo_id_autentique?: string | null;
    /** Retrato das faturas originais. Sem ele (e sem termo), é acordo antigo. */
    faturas_origem_snapshot?: unknown[] | null;
    termo_signatario_cliente_email?: string | null;
    termo_url_coobrigado?: string | null;
    /** Código do link público do acordo (#6204). */
    token_publico?: string | null;
    termo_dados?: DadosTermo | null;
    termo_numero?: string | null;
    termo_assinado_em?: string | null;
    entrada_paga_em?: string | null;
    expira_em?: string | null;
    efetivado_em?: string | null;
    cancelamento_motivo?: string | null;
    aprovacao_necessaria?: boolean | null;
    saldo_kwh?: number | null;
    /** 'fatura_vigor' = parcelas somadas no boleto mensal (17/10/2026). */
    cobranca_parcelas?: 'avulsa' | 'fatura_vigor' | null;
    unidade_consumidora: {
        id: number;
        nome_cliente: string;
        numero_uc: string;
        outro_numero_uc: string | null;
    } | null;
    parcelamento_parcelas: Parcela[];
}

interface Parcela {
    id: string;
    numero_parcela: number;
    valor: number;
    mes_referencia: string;
    data_vencimento: string;
    status: 'pendente' | 'provisionado' | 'pago' | 'cancelado';
    fatura_id: string | null;
    /** UC e mês da fatura ligada — é por eles que o clique abre a fatura em Faturas.
     *  `parcelamento_origem_id` diferente do acordo = parcela somada na fatura mensal.
     *  O resto diz em que pé está o boleto da parcela (#6540). */
    fatura?: {
        uc_codigo: string | null; mes_referencia: string | null; parcelamento_origem_id?: string | null;
        status_fatura?: string | null; num_cobranca_boleto?: string | null; status_boleto_vigor?: string | null;
        vencimento_boleto?: string | null; data_pgto_boleto?: string | null; quitado_em?: string | null;
    } | null;
}

/** Confirmação aberta na tela — uma de cada vez. */
type DialogoAcordo =
    | { tipo: 'cancelar' | 'efetivar' | 'aprovar'; acordoId: string }
    | { tipo: 'termo'; acordo: any };

/** Mesma exigência da edge function e de `cancelarAcordo`/`efetivarAcordoManual`. */
const MOTIVO_MINIMO = 10;

const ParcelamentosView: React.FC = () => {
    const { user, profile } = useAuth();
    // Mesma alçada da edge function para efetivar/cancelar.
    const podeAprovar = profile?.role === 'adm' || profile?.role === 'gerente';
    const { addToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [acordos, setAcordos] = useState<Acordo[]>([]);
    
    // Deep-link ?novo=1&faturas=<ids>: a mensagem de WhatsApp da rota de cobrança
    // traz esse link para o cobrador abrir o parcelamento já com as faturas da UC
    // selecionadas, na porta do cliente, sem procurar nada.
    // E ?busca=<código>&status=todos: o "Abrir em Parcelamentos" do card da rota
    // cai direto no acordo — o código é o começo do id, como o termo imprime.
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();

    // Clique na parcela abre a fatura dela em Faturas, já filtrada pela UC e pelo
    // mês. Antes era copiar a UC, ir para lá e escolher o mês na mão. Ctrl/Cmd
    // abre em outra aba, para não perder o acordo aberto.
    const abrirFaturaDaParcela = (p: Parcela, acordo: Acordo, e: React.MouseEvent) => {
        if (!p.fatura) return;
        const uc = p.fatura.uc_codigo || acordo.unidade_consumidora?.numero_uc || acordo.uc_codigo_snapshot || '';
        const mes = p.fatura.mes_referencia || p.mes_referencia;
        const rota = `/financeiro/faturas?busca=${encodeURIComponent(uc)}&mes=${encodeURIComponent(mes)}`;
        if (e.ctrlKey || e.metaKey) window.open(`#${rota}`, '_blank');
        else navigate(rota);
    };

    // Filters & UI State
    const [searchTerm, setSearchTerm] = useState(() => params.get('busca') ?? '');
    // Padrão 'em_andamento' = tudo que ainda está vivo. Com 'ativo' puro, os
    // acordos em atraso (os que mais pedem atenção) e a proposta recém-criada
    // sumiam da tela de abertura.
    const [statusFilter, setStatusFilter] = useState<string>(() => {
        const doLink = params.get('status');
        return doLink && (doLink === 'todos' || doLink in ROTULO_STATUS) ? doLink : 'em_andamento';
    });
    const [expandedAcordo, setExpandedAcordo] = useState<string | null>(null);
    // #6587: vindo da UC citada numa solicitação (?abrir=unico), abre o acordo direto
    // quando a busca devolve um só.
    const abrirUnicoRef = useRef(params.get('abrir') === 'unico');
    useEffect(() => {
        if (!abrirUnicoRef.current || loading) return;
        abrirUnicoRef.current = false;
        if (acordos.length === 1) setExpandedAcordo(String(acordos[0].id));
    }, [loading, acordos]);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    // Confirmações da tela. Eram `prompt`/`confirm` do navegador: fora do padrão
    // visual, sem validação enquanto se digita e com a caixa anunciando
    // "localhost:5173 diz" para o analista.
    const [dialogo, setDialogo] = useState<DialogoAcordo | null>(null);
    const [motivoDialogo, setMotivoDialogo] = useState('');
    const [emailTermo, setEmailTermo] = useState('');
    const [whatsappTermo, setWhatsappTermo] = useState('');
    // Responsável pela dívida (coobrigado), quando o acordo tem um.
    const [emailCoobTermo, setEmailCoobTermo] = useState('');
    const [whatsappCoobTermo, setWhatsappCoobTermo] = useState('');
    const [cancelingId, setCancelingId] = useState<string | null>(null);
    // 'acordos' = cronogramas; 'conferencia' = divergências entre o acordo e o
    // que o banco ainda cobra (boleto vivo, pagamento após acordo, fila).
    // ?aba=emitir: link do aviso no sino das parcelas sem boleto (#6205).
    const [secao, setSecao] = useState<'acordos' | 'conferencia' | 'emitir'>(() => (params.get('aba') === 'emitir' ? 'emitir' : 'acordos'));
    const [qtdParaEmitir, setQtdParaEmitir] = useState<number | null>(null);
    // Botão da parcela no cronograma: abre a fatura dela na tela de Faturas daquele
    // passo (emitir, enviar, cobrança enviada) sem sair do acordo (#6540).
    const { abrir: abrirFaturaParcela, abrindo: abrindoFaturaParcela, telas: telasFaturaParcela } = useFaturaDaParcela(() => fetchAcordos());

    const [faturasPreSelecionadas, setFaturasPreSelecionadas] = useState<any[] | undefined>(undefined);

    // Paginacao simples
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 10;
    const [hasMore, setHasMore] = useState(false);

    useEffect(() => {
        if (params.get('novo') !== '1') return;
        const ids = (params.get('faturas') || '').split(',').map(v => v.trim()).filter(Boolean);

        // Limpa a query ANTES de abrir: sem isso, fechar o modal e recarregar a
        // página reabriria o parcelamento sozinho.
        setParams({}, { replace: true });

        if (ids.length === 0) { setIsCreateModalOpen(true); return; }

        (async () => {
            const { data, error } = await supabase
                .from('faturas')
                // num_cobranca_boleto é obrigatório na prática: sem ele o aviso de
                // boleto Sicoob quebra ao gerar as parcelas.
                .select('id, uc_codigo, cliente_nome, mes_referencia, valor_boleto_vigor, vencimento_boleto, status_boleto_vigor, status_fatura, num_cobranca_boleto')
                .in('id', ids);
            if (error || !data || data.length === 0) {
                addToast('Não consegui carregar as faturas do link. Busque-as manualmente.', 'error');
                setIsCreateModalOpen(true);
                return;
            }
            if (data.length < ids.length) {
                addToast(`${data.length} de ${ids.length} faturas do link foram carregadas.`, 'info');
            }
            setFaturasPreSelecionadas(data);
            setIsCreateModalOpen(true);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchAcordos = async () => {
        setLoading(true);
        try {
            let query = supabase
                .from('parcelamentos')
                .select(`
                    *,
                    unidade_consumidora:unidades_consumidoras(id, nome_cliente, numero_uc, outro_numero_uc),
                    parcelamento_parcelas(*, fatura:faturas!parcelamento_parcelas_fatura_id_fkey(uc_codigo, mes_referencia, parcelamento_origem_id, status_fatura, num_cobranca_boleto, status_boleto_vigor, vencimento_boleto, data_pgto_boleto, quitado_em))
                `, { count: 'exact' });

            // Apply Status Filter
            if (statusFilter === 'em_andamento') {
                query = query.in('status', [...ESTADOS_PENDENTES, 'ativo', 'em_atraso']);
            } else if (statusFilter !== 'todos') {
                query = query.eq('status', statusFilter);
            }

            // Se tiver termo de busca textual, nós aplicamos localmente ou via join
            // Para manter compatível com o supabase v2 client de forma simples:
            query = query.order('created_at', { ascending: false });
            
            const { data, error, count } = await query;
            if (error) throw error;

            let parsed = (data as any[]) || [];

            // Situação do boleto da entrada, para os avisos do card ("não chegou").
            // Uma consulta só para a lista inteira, em vez de embed: faturas e
            // parcelamentos se ligam por duas chaves, e o embed ficaria ambíguo.
            const idsEntrada = parsed.map(ac => ac.entrada_fatura_id).filter(Boolean);
            if (idsEntrada.length > 0) {
                const { data: entradas } = await supabase
                    .from('faturas')
                    .select('id, num_cobranca_boleto, status_boleto_vigor, vencimento_boleto')
                    .in('id', idsEntrada);
                const porId = new Map((entradas || []).map((f: any) => [String(f.id), f]));
                parsed = parsed.map(ac => ({ ...ac, entrada_fatura: porId.get(String(ac.entrada_fatura_id)) || null }));
            }

            // Filtragem por texto (localmente, já que a base é relativamente leve por query inicial)
            if (searchTerm.trim()) {
                const s = searchTerm.toLowerCase();
                parsed = parsed.filter(ac => {
                    const nome = (ac.unidade_consumidora?.nome_cliente || ac.cliente_nome_snapshot || '').toLowerCase();
                    const uc = (ac.unidade_consumidora?.numero_uc || ac.uc_codigo_snapshot || '').toLowerCase();
                    return nome.includes(s) || uc.includes(s) || (ac.descricao && ac.descricao.toLowerCase().includes(s))
                        || String(ac.id).toLowerCase().startsWith(s.trim());
                });
            }

            setAcordos(parsed);
            
            // Se houver algum item, abre o primeiro por padrão caso nenhum esteja aberto
            if (parsed.length > 0 && !expandedAcordo) {
                setExpandedAcordo(parsed[0].id);
            }
        } catch (err: any) {
            console.error(err);
            addToast('Erro ao carregar parcelamentos: ' + err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAcordos();
    }, [statusFilter, searchTerm]);

    const toggleExpand = (id: string) => {
        setExpandedAcordo(prev => prev === id ? null : id);
    };

    const abrirDialogo = (d: DialogoAcordo) => {
        setMotivoDialogo('');
        if (d.tipo === 'termo') {
            const dados: DadosTermo | null = d.acordo.termo_dados ?? null;
            setEmailTermo(dados?.devedor.email || d.acordo.termo_signatario_cliente_email || '');
            setWhatsappTermo(dados?.devedor.whatsapp || '');
            setEmailCoobTermo(dados?.coobrigado?.email || '');
            setWhatsappCoobTermo(dados?.coobrigado?.whatsapp || '');
            // Sem contato gravado no acordo, busca no cadastro — antes o campo
            // vinha vazio e o analista tinha de ir procurar o e-mail do cliente.
            if (!dados?.devedor.email && !d.acordo.termo_signatario_cliente_email) {
                buscarCadastroDevedor({ ucId: d.acordo.unidade_consumidora_id, ucCodigo: d.acordo.uc_codigo_snapshot })
                    .then(({ uc, negociacao }) => {
                        const padrao = devedorPadrao({ uc, negociacao });
                        setEmailTermo(prev => prev || padrao.email);
                        setWhatsappTermo(prev => prev || padrao.whatsapp);
                    })
                    .catch(err => console.error('[parcelamentos] cadastro do devedor', err));
            }
        }
        setDialogo(d);
    };

    const fecharDialogo = () => {
        if (cancelingId) return; // ação em voo: fechar só confundiria
        setDialogo(null);
        setMotivoDialogo('');
    };

    /** Fecha antes de agir: o toast de erro fica ATRÁS do modal (mesmo z-index),
     *  e a linha do acordo já mostra "Processando…" enquanto a ação roda. */
    const confirmarDialogo = () => {
        const d = dialogo;
        if (!d) return;
        const motivo = motivoDialogo.trim();
        setDialogo(null);
        setMotivoDialogo('');
        if (d.tipo === 'termo') {
            executarEnvioTermo(d.acordo, {
                devedor: { email: emailTermo.trim(), whatsapp: whatsappTermo.trim() },
                coobrigado: d.acordo.termo_dados?.coobrigado
                    ? { email: emailCoobTermo.trim(), whatsapp: whatsappCoobTermo.trim() }
                    : null,
            });
            return;
        }
        if (d.tipo === 'cancelar') executarCancelamento(d.acordoId, motivo);
        else if (d.tipo === 'efetivar') executarEfetivacao(d.acordoId, motivo);
        else executarAprovacao(d.acordoId);
    };

    const pedeMotivo = dialogo?.tipo === 'cancelar' || dialogo?.tipo === 'efetivar';
    const confirmarDesabilitado = pedeMotivo
        ? motivoDialogo.trim().length < MOTIVO_MINIMO
        : dialogo?.tipo === 'termo'
            ? !emailValido(emailTermo) || (!!dialogo.acordo.termo_dados?.coobrigado && !emailValido(emailCoobTermo))
            : false;

    /**
     * Cancelamento de acordo.
     *
     * Passa pela edge function, não por UPDATE direto. A versão anterior só
     * marcava o cabeçalho como 'cancelado' e as parcelas 'pendente' — e isso
     * virou um buraco no momento em que o acordo passou a carimbar as faturas-mãe
     * como 'Parcelado': a dívida original ficava apagada, sem cobrança nova no
     * lugar e sem ninguém saber. A function devolve as faturas-mãe ao estado
     * que estava guardado no snapshot e cancela as cobranças do acordo.
     */
    const executarCancelamento = async (acordoId: string, motivo: string) => {
        setCancelingId(acordoId);
        try {
            const r: any = await cancelarAcordo(acordoId, motivo);
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'parcelamentos', acao: 'cancelar_parcelamento', origem: 'acao' });
            addToast(
                `Acordo cancelado. ${r?.faturasCanceladas ?? 0} cobrança(s) do acordo cancelada(s)` +
                `${r?.baixaPedida ? ` (${r.baixaPedida} com baixa do boleto enviada ao Sicoob)` : ''}, ` +
                `${r?.restauradas ?? 0} fatura(s) original(is) devolvida(s) à cobrança.`,
                'success', 12000,
            );
            // Cada aviso é uma pendência de dinheiro que o sistema não resolve
            // sozinho (crédito a abater, boleto a reemitir) — um toast por aviso
            // para nenhum se perder no meio de um texto longo.
            for (const aviso of (r?.avisos as string[] | undefined) ?? []) {
                addToast(`⚠️ ${aviso.charAt(0).toUpperCase()}${aviso.slice(1)}.`, 'warning', 20000);
            }
            fetchAcordos();
        } catch (err: any) {
            console.error(err);
            addToast('Erro ao cancelar acordo: ' + (err.message || err), 'error', 12000);
        } finally {
            setCancelingId(null);
        }
    };

    /** Envio do termo de uma proposta que nasceu sem ele (envio desmarcado ou
     *  falha do fluxo na criação). */
    const executarEnvioTermo = async (acordo: any, contatos: Parameters<typeof enviarTermoAcordo>[1]) => {
        setCancelingId(acordo.id);
        try {
            const resposta = await enviarTermoAcordo(acordo.id, contatos, { id: user?.id, nome: profile?.full_name ?? null });
            addToast('Termo enviado para o cliente assinar' + descreverCanal(resposta) + '.', resposta.canal_devedor === 'email' ? 'warning' : 'success', 10000);
            const entrada = mensagemEnvioEntrada(resposta.entrada);
            if (entrada) addToast(entrada.texto, entrada.tipo, 15000);
            fetchAcordos();
        } catch (err: any) {
            console.error(err);
            addToast('O termo NÃO foi enviado: ' + (err.message || err), 'error', 15000);
        } finally {
            setCancelingId(null);
        }
    };

    /** "Emitir e enviar de novo" do aviso do card: emite (se ainda não tem
     *  boleto) e manda só pelo canal que falhou. Sem assinatura, só emite: o
     *  boleto vai para o cliente quando ele assinar. */
    const executarReenvioEntrada = async (acordoId: string) => {
        setCancelingId(acordoId);
        try {
            const r = await emitirEEnviarEntrada(acordoId, { id: user?.id, nome: profile?.full_name ?? null }, { soQueFalhou: true });
            const m = mensagemEnvioEntrada(r);
            if (m) addToast(m.texto, m.tipo, 15000);
            fetchAcordos();
        } catch (err: any) {
            console.error(err);
            addToast('Não foi possível reenviar a entrada: ' + (err.message || err), 'error', 12000);
        } finally {
            setCancelingId(null);
        }
    };

    const executarAprovacao = async (acordoId: string) => {
        setCancelingId(acordoId);
        try {
            await aprovarAcordo(acordoId, { id: user?.id, nome: profile?.full_name ?? null });
            addToast('Proposta aprovada. Agora envie o termo para o cliente assinar.', 'success', 10000);
            fetchAcordos();
        } catch (err: any) {
            console.error(err);
            addToast('Não foi possível aprovar: ' + (err.message || err), 'error', 12000);
        } finally {
            setCancelingId(null);
        }
    };

    /** Efetivação manual — para quando o analista tem o comprovante em mãos e
     *  não quer esperar a varredura. Mesma edge function do cron. */
    const executarEfetivacao = async (acordoId: string, motivo: string) => {
        setCancelingId(acordoId);
        try {
            const r: any = await efetivarAcordoManual(acordoId, motivo);
            addToast(
                `Acordo efetivado: ${r?.criadas ?? 0} parcela(s) viraram cobrança e ${r?.faturasMarcadas ?? 0} fatura(s) foram marcadas como Parcelado.`,
                'success', 12000,
            );
            fetchAcordos();
        } catch (err: any) {
            console.error(err);
            addToast('Não foi possível efetivar: ' + (err.message || err), 'error', 12000);
        } finally {
            setCancelingId(null);
        }
    };

    /** Cores por estado. Os três estados pendentes ficam em âmbar de propósito:
     *  eles são "ainda não vale", e confundi-los com 'ativo' é justamente o erro
     *  que faria alguém achar que a dívida antiga já foi substituída. */
    const CORES_STATUS: Record<StatusAcordo, string> = {
        aguardando_aprovacao: 'bg-purple-50 dark:bg-purple-950 text-purple-600 dark:text-purple-400 border-purple-100 dark:border-purple-900',
        aguardando_assinatura: 'bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400 border-amber-100 dark:border-amber-900',
        aguardando_entrada: 'bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
        ativo: 'bg-green-50 dark:bg-green-950 text-green-600 dark:text-green-400 border-green-100 dark:border-green-900',
        em_atraso: 'bg-orange-50 dark:bg-orange-950 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-900',
        concluido: 'bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-900',
        cancelado: 'bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 border-red-100 dark:border-red-900',
        expirado: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700',
    };

    const getStatusBadge = (status: string) => {
        const cor = CORES_STATUS[status as StatusAcordo];
        if (!cor) return null;
        return (
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${cor}`}>
                {ROTULO_STATUS[status as StatusAcordo] ?? status}
            </span>
        );
    };

    // Situação do boleto de cada parcela (#6540). Antes era Pendente/Faturado/Pago:
    // "Faturado" valia tanto para a fatura só calculada quanto para o boleto já
    // enviado, e não dava para saber o que faltava fazer.
    const badgeDaParcela = (p: Parcela, acordo: Acordo, etapa: EtapaBoleto, cedo: boolean) => {
        const vale = acordo.status === 'ativo' || acordo.status === 'em_atraso';
        // Acordo que soma as parcelas no boleto mensal: a parcela espera a fatura do mês.
        if (acordo.cobranca_parcelas === 'fatura_vigor' && vale && p.status === 'pendente' && !p.fatura_id) {
            return <span title={`Entra no boleto da fatura de ${p.mes_referencia}. Se não entrar até ${DIAS_PARA_SAIR_SEPARADA} dias antes de vencer, sai em boleto separado.`} className="inline-block whitespace-nowrap px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 rounded dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">Aguarda fatura</span>;
        }
        const titulo = etapa === 'no_boleto_mensal'
            ? `Somada no boleto da fatura de ${p.fatura?.mes_referencia || p.mes_referencia}`
            : etapa === 'sem_fatura'
                ? (vale ? 'Parcela ainda sem fatura' : 'As faturas das parcelas nascem quando o acordo passa a valer')
                : etapa === 'a_enviar'
                    ? 'Boleto emitido que ainda não foi enviado ao associado'
                    : cedo
                        ? `Entra em "Emitir e enviar" ${DIAS_ANTES_EMITIR} dias antes do vencimento. Pode emitir antes, se preferir.`
                        : undefined;
        return <EtapaBoletoBadge etapa={etapa} title={titulo} cedo={cedo} />;
    };
    const hoje = new Date(Date.now() - 3 * 3600_000).toISOString().slice(0, 10);

    return (
        <div className="flex-1 bg-background dark:bg-gray-950 flex flex-col overflow-hidden">
            {/* Header da View */}
            <div className="px-6 py-5 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
                <div>
                    <h1 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-xl text-primary">
                            <Banknote size={24} />
                        </div>
                        Gestão de Parcelamentos
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mt-1">
                        Controle acordos de renegociação de dívidas e acompanhe cronogramas de parcelamento.
                    </p>
                </div>
                
                <div className="flex items-center gap-2 self-start sm:self-auto">
                    {/* Alterna entre os cronogramas e a conferência de divergências.
                        Conferência mora aqui porque o que ela mostra é sempre
                        consequência de um acordo — separar em rota própria afastaria
                        do contexto em que o operador já está. */}
                    <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
                        <button
                            onClick={() => setSecao('acordos')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${secao === 'acordos' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
                        >
                            Acordos
                        </button>
                        <button
                            onClick={() => setSecao('conferencia')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${secao === 'conferencia' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
                        >
                            Conferência
                        </button>
                        <button
                            onClick={() => setSecao('emitir')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${secao === 'emitir' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
                        >
                            Emitir e enviar{qtdParaEmitir ? <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-[10px] text-white">{qtdParaEmitir}</span> : null}
                        </button>
                    </div>

                    {secao === 'acordos' ? (
                        <button
                            onClick={() => setIsCreateModalOpen(true)}
                            className="px-4 py-2.5 bg-primary text-white font-bold rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl hover:scale-[1.01] transition-all flex items-center gap-2"
                        >
                            <Plus size={18} /> Novo Acordo
                        </button>
                    ) : null}
                </div>
            </div>

            {secao === 'emitir' ? (
                <div className="flex-1 overflow-y-auto p-6">
                    <ParcelasParaEmitir onContagem={setQtdParaEmitir} />
                </div>
            ) : secao === 'conferencia' ? (
                <div className="flex-1 overflow-y-auto p-6">
                    <ConferenciaAcordosView />
                </div>
            ) : (
            <>
            {/* Filtros */}
            <div className="px-6 py-4 bg-gray-50/50 dark:bg-gray-900/30 border-b border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row items-stretch gap-3 shrink-0">
                {/* Barra de Busca */}
                <div className="relative flex-1 min-w-[260px]">
                    <Search className="absolute left-3 top-3 text-gray-400" size={18} />
                    <input 
                        type="text" 
                        placeholder="Buscar por cliente, UC ou descrição..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary font-medium transition-all"
                    />
                </div>

                {/* Filtro de Status */}
                <div className="flex items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-2 overflow-hidden">
                    <Filter size={16} className="ml-2 text-gray-400 mr-1" />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="py-2.5 px-2 bg-transparent border-none outline-none text-sm font-bold text-gray-600 dark:text-gray-300 cursor-pointer select-none"
                    >
                        <option value="em_andamento">Em andamento</option>
                        <option value="todos">Todos os Status</option>
                        <option value="aguardando_aprovacao">Aguardando aprovação</option>
                        <option value="aguardando_assinatura">Aguardando assinatura</option>
                        <option value="aguardando_entrada">Aguardando entrada</option>
                        <option value="ativo">Ativos (em dia)</option>
                        <option value="em_atraso">Em atraso</option>
                        <option value="concluido">Concluídos</option>
                        <option value="cancelado">Cancelados</option>
                        <option value="expirado">Expirados</option>
                    </select>
                </div>
            </div>

            {/* Grid/Lista Principal */}
            <div className="flex-1 overflow-y-auto p-6">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                        <Loader2 className="animate-spin text-primary mb-4" size={40} />
                        <p className="font-bold text-sm animate-pulse">Carregando cronogramas...</p>
                    </div>
                ) : acordos.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center max-w-md mx-auto bg-white dark:bg-gray-900 rounded-3xl border border-dashed border-gray-200 dark:border-gray-800 shadow-sm p-8 animate-fade-in">
                        <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-2xl flex items-center justify-center text-gray-400 dark:text-gray-600 mb-4">
                            <Banknote size={32} />
                        </div>
                        <h3 className="text-lg font-black text-gray-800 dark:text-gray-200">Nenhum acordo encontrado</h3>
                        <p className="text-sm text-gray-500 mt-2 mb-6">
                            Não há acordos de parcelamento cadastrados com os filtros atuais. Crie um novo acordo para começar a rastrear cronogramas.
                        </p>
                        <button
                            onClick={() => setIsCreateModalOpen(true)}
                            className="px-5 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-primary hover:text-white text-gray-700 dark:text-gray-300 font-bold rounded-xl transition-all flex items-center gap-2 text-sm shadow-sm"
                        >
                            Criar Primeiro Acordo <Plus size={16} />
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4 animate-fade-in">
                        {acordos.map((acordo) => {
                            const totalPago = acordo.parcelamento_parcelas
                                .filter(p => p.status === 'pago')
                                .reduce((sum, p) => sum + p.valor, 0);
                            const totalPendente = acordo.parcelamento_parcelas
                                .filter(p => p.status === 'pendente' || p.status === 'provisionado')
                                .reduce((sum, p) => sum + p.valor, 0);
                            
                            const countTotal = acordo.parcelamento_parcelas.length;
                            const countPagas = acordo.parcelamento_parcelas.filter(p => p.status === 'pago').length;
                            
                            const pctProgresso = countTotal > 0 ? Math.round((countPagas / countTotal) * 100) : 0;
                            const isExpanded = expandedAcordo === acordo.id;
                            const avisos: AvisoAcordo[] = avisosDoAcordo({ ...acordo, entrada: acordo.entrada_fatura });

                            return (
                                <div 
                                    key={acordo.id}
                                    className={`bg-white dark:bg-gray-900 rounded-2xl border transition-all overflow-hidden ${
                                        isExpanded 
                                            ? 'border-primary/30 shadow-xl dark:shadow-black/40 shadow-primary/5 scale-[1.005]' 
                                            : 'border-gray-100 dark:border-gray-800 hover:border-gray-300 hover:shadow-md'
                                    }`}
                                >
                                    {/* Card Header clickable */}
                                    <div 
                                        onClick={() => toggleExpand(acordo.id)}
                                        className="p-5 cursor-pointer flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors"
                                    >
                                        <div className="flex items-start gap-4 min-w-0 flex-1">
                                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                                                acordo.status === 'cancelado' ? 'bg-gray-100 dark:bg-gray-800 text-gray-400' : 'bg-primary/10 text-primary'
                                            }`}>
                                                <Building2 size={22} />
                                            </div>
                                            
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                                    <h4 className="text-base font-extrabold text-gray-900 dark:text-white truncate max-w-xs sm:max-w-md">
                                                        {acordo.unidade_consumidora?.nome_cliente || acordo.cliente_nome_snapshot || 'Acordo avulso'}
                                                    </h4>
                                                    {getStatusBadge(acordo.status)}
                                                    {acordo.cobranca_parcelas === 'fatura_vigor' ? (
                                                        <span title="As parcelas entram no boleto mensal da Vigor" className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-primary/5 text-primary border-primary/20">
                                                            Parcelas no boleto da Vigor
                                                        </span>
                                                    ) : null}
                                                    {avisos.length > 0 ? (
                                                        <span
                                                            title={avisos.map(av => `${av.titulo}: ${av.detalhe}`).join('\n')}
                                                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${avisos.some(av => av.nivel === 'erro')
                                                                ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
                                                                : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800'}`}
                                                        >
                                                            <AlertTriangle size={11} /> {avisos[0].titulo}{avisos.length > 1 ? ` (+${avisos.length - 1})` : ''}
                                                        </span>
                                                    ) : null}
                                                </div>

                                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 leading-tight mb-2">
                                                    UC: {acordo.unidade_consumidora?.numero_uc || acordo.unidade_consumidora?.outro_numero_uc || acordo.uc_codigo_snapshot || '—'}
                                                    {acordo.descricao && ` • ${acordo.descricao}`}
                                                </p>

                                                {/* Progress Bar */}
                                                <div className="flex items-center gap-3 max-w-xs">
                                                    <div className="h-1.5 flex-1 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                                                        <div 
                                                            className={`h-full rounded-full transition-all duration-500 ${
                                                                acordo.status === 'cancelado' ? 'bg-gray-300 dark:bg-gray-600' : 'bg-primary'
                                                            }`}
                                                            style={{ width: `${pctProgresso}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="text-[10px] font-bold text-gray-400 uppercase whitespace-nowrap">
                                                        {countPagas}/{countTotal} parcelas ({pctProgresso}%)
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center flex-wrap lg:flex-nowrap gap-4 sm:gap-6 ml-auto lg:ml-0 shrink-0 w-full lg:w-auto pt-4 lg:pt-0 border-t lg:border-t-0 border-gray-100 dark:border-gray-800 justify-between sm:justify-end">
                                            <div className="text-left sm:text-right">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase block">Valor Total</span>
                                                <div className="font-black text-gray-900 dark:text-white text-lg leading-none">
                                                    R$ {acordo.valor_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                </div>
                                            </div>

                                            <div className="text-left sm:text-right hidden sm:block">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase block">Quitado / Em Aberto</span>
                                                <div className="text-xs font-bold text-gray-700 dark:text-gray-300 mt-0.5">
                                                    <span className="text-emerald-600">R$ {totalPago.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                                    <span className="text-gray-300 dark:text-gray-600 mx-1">|</span>
                                                    <span className="text-amber-600">R$ {totalPendente.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 pl-4 border-l border-gray-100 dark:border-gray-800">
                                                {isExpanded ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Inner List (Expanded Content) */}
                                    {isExpanded && (
                                        <div className="border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 animate-fade-in flex flex-col lg:flex-row overflow-hidden">
                                            
                                            {/* Left Panel: Cronograma */}
                                            <div className="flex-1 p-5 border-b lg:border-b-0 lg:border-r border-gray-100 dark:border-gray-800">
                                                <h5 className="text-xs font-black text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-1.5">
                                                    <Calendar size={12} className="text-primary" /> Cronograma Detalhado de Parcelas
                                                </h5>

                                                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/60 dark:border-gray-750 shadow-sm overflow-x-auto">
                                                    <table className="w-full text-left border-collapse text-sm">
                                                        <thead className="bg-gray-50 dark:bg-gray-750 text-[10px] font-black text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-700">
                                                            <tr>
                                                                <th className="px-4 py-2.5 text-center w-16">Nº</th>
                                                                <th className="px-4 py-2.5">Mês Ref.</th>
                                                                <th className="px-4 py-2.5">Vencimento</th>
                                                                <th className="px-4 py-2.5">Valor</th>
                                                                <th className="px-4 py-2.5 text-center">Boleto</th>
                                                                <th className="px-4 py-2.5"><span className="sr-only">Ação</span></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-50 dark:divide-gray-750">
                                                            {acordo.parcelamento_parcelas
                                                                .sort((a, b) => a.numero_parcela - b.numero_parcela)
                                                                .map((p) => {
                                                                    const etapa = etapaBoletoParcela(p, p.fatura, acordo.id, hoje);
                                                                    const acao = ACAO_ETAPA[etapa];
                                                                    // Emitir e enviar só em acordo que vale; ver a cobrança enviada, sempre.
                                                                    const podeAgir = !!acao && !!p.fatura_id
                                                                        && (acao === 'abrir' || acordo.status === 'ativo' || acordo.status === 'em_atraso');
                                                                    const faturaId = Number(p.fatura_id);
                                                                    // Fora da janela de emissão: dá para emitir, mas nada pede pressa.
                                                                    const cedo = etapa === 'sem_boleto' && diasEntre(hoje, p.data_vencimento) > DIAS_ANTES_EMITIR;
                                                                    return (
                                                                    <tr
                                                                        key={p.id}
                                                                        onClick={p.fatura ? (e) => abrirFaturaDaParcela(p, acordo, e) : undefined}
                                                                        title={p.fatura ? `Abrir a fatura de ${p.fatura.mes_referencia || p.mes_referencia} em Faturas` : 'Parcela ainda sem fatura'}
                                                                        className={`transition-colors ${p.fatura
                                                                            ? 'cursor-pointer hover:bg-primary/5 dark:hover:bg-primary/10'
                                                                            : 'hover:bg-gray-50/30 dark:hover:bg-gray-700/20'} ${
                                                                        p.status === 'cancelado' ? 'opacity-50 line-through decoration-gray-300 text-gray-400' : ''
                                                                    }`}>
                                                                        <td className="px-4 py-3 text-center">
                                                                            <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-100 dark:bg-gray-700 rounded-full text-xs font-bold text-gray-600 dark:text-gray-300">
                                                                                {p.numero_parcela}
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-4 py-3 font-bold text-gray-700 dark:text-gray-300">{p.mes_referencia}</td>
                                                                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                                                                            {new Date(p.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR')}
                                                                        </td>
                                                                        <td className="px-4 py-3 font-black text-gray-900 dark:text-white">
                                                                            R$ {p.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                                        </td>
                                                                        <td className="px-4 py-3 text-center">
                                                                            {badgeDaParcela(p, acordo, etapa, cedo)}
                                                                        </td>
                                                                        <td className="px-3 py-2 text-right">
                                                                            {podeAgir ? (
                                                                                <button
                                                                                    type="button"
                                                                                    // A linha abre a fatura em Faturas; o botão abre aqui mesmo.
                                                                                    onClick={(e) => { e.stopPropagation(); abrirFaturaParcela(faturaId); }}
                                                                                    disabled={abrindoFaturaParcela === faturaId}
                                                                                    title={acao === 'emitir' ? 'Emitir o boleto desta parcela'
                                                                                        : acao === 'enviar' ? 'Enviar o boleto ao associado (WhatsApp e e-mail)'
                                                                                            : 'Ver o boleto enviado, reenviar o e-mail ou prorrogar'}
                                                                                    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-lg px-2.5 py-1 text-[11px] font-bold transition-[background-color,transform] duration-150 ease-out active:scale-[0.97] disabled:opacity-60 ${acao === 'abrir' || cedo
                                                                                        ? 'border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                                                                                        : 'bg-primary text-white hover:bg-primary/90'}`}
                                                                                >
                                                                                    {abrindoFaturaParcela === faturaId ? <Loader2 size={12} className="animate-spin" />
                                                                                        : acao === 'enviar' ? <Send size={12} /> : <FileText size={12} />}
                                                                                    {acao === 'emitir' ? 'Emitir' : acao === 'enviar' ? 'Enviar' : 'Abrir'}
                                                                                </button>
                                                                            ) : null}
                                                                        </td>
                                                                    </tr>
                                                                    );
                                                                })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>

                                            {/* Painel direito: situação, trilha e ações */}
                                            <div className="w-full lg:w-80 p-5 bg-white/50 dark:bg-gray-900/50 flex flex-col justify-between gap-4">
                                                <div className="space-y-4">
                                                    <div className="space-y-1">
                                                        <span className="text-[10px] font-bold text-gray-400 uppercase">Criado em</span>
                                                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                                            {new Date(acordo.created_at).toLocaleString('pt-BR')}
                                                        </p>
                                                    </div>

                                                    {avisos.map((av, i) => (
                                                        <div
                                                            key={i}
                                                            className={`rounded-xl border p-3 text-xs space-y-2 ${av.nivel === 'erro'
                                                                ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'
                                                                : 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300'}`}
                                                        >
                                                            <p className="font-bold flex items-center gap-1.5"><AlertTriangle size={13} /> {av.titulo}</p>
                                                            <p className="leading-snug">{av.detalhe}</p>
                                                            {av.acao === 'reenviar_entrada' ? (
                                                                <button
                                                                    onClick={() => executarReenvioEntrada(acordo.id)}
                                                                    disabled={cancelingId === acordo.id}
                                                                    className="w-full px-3 py-1.5 bg-white dark:bg-gray-900 font-bold rounded-lg border border-current flex items-center justify-center gap-2 disabled:opacity-50"
                                                                >
                                                                    {cancelingId === acordo.id
                                                                        ? <><Loader2 className="animate-spin" size={13} /> Enviando…</>
                                                                        : acordo.status === 'aguardando_assinatura'
                                                                            ? <><Send size={13} /> Emitir o boleto</>
                                                                            : <><Send size={13} /> Emitir e enviar de novo</>}
                                                                </button>
                                                            ) : null}
                                                        </div>
                                                    ))}

                                                    {/* #6207: link da página pública do acordo, para mandar ao associado */}
                                                    {acordo.token_publico && !['cancelado', 'expirado', 'rejeitado'].includes(acordo.status) ? (
                                                        <div className="flex flex-col items-stretch gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={async () => {
                                                                    const link = `${window.location.origin}/#/acordo/${acordo.token_publico}`;
                                                                    try {
                                                                        await navigator.clipboard.writeText(link);
                                                                        addToast('Link do acordo copiado. Ele mostra todas as parcelas e os boletos já emitidos.', 'success');
                                                                    } catch {
                                                                        addToast(`Não consegui copiar. Link: ${link}`, 'warning', 15000);
                                                                    }
                                                                }}
                                                                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs font-semibold text-gray-700 dark:text-slate-200 transition-colors hover:bg-gray-50 dark:hover:bg-slate-800"
                                                            >
                                                                <Link2 size={13} /> Copiar link do acordo
                                                            </button>
                                                            <a
                                                                href={`${window.location.origin}/#/acordo/${acordo.token_publico}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="inline-flex min-h-[32px] items-center justify-center gap-1 text-xs font-medium text-primary dark:text-primary-light hover:underline"
                                                            >
                                                                Ver como o associado vê <ExternalLink size={12} aria-hidden="true" />
                                                                <span className="sr-only">(abre em nova aba)</span>
                                                            </a>
                                                        </div>
                                                    ) : null}

                                                    <AcordoTrilhaPanel
                                                        acordoId={acordo.id}
                                                        status={acordo.status}
                                                        termoUrl={acordo.termo_url}
                                                        termoUrlCoobrigado={acordo.termo_url_coobrigado}
                                                        termoNumero={acordo.termo_numero}
                                                        termoAssinadoEm={acordo.termo_assinado_em}
                                                        termoEntrega={acordo.termo_entrega}
                                                        entradaValor={acordo.entrada_valor}
                                                        entradaVencimento={acordo.entrada_vencimento}
                                                        entradaPagaEm={acordo.entrada_paga_em}
                                                        // A data só é gravada na efetivação; antes disso a cobrança da
                                                        // entrada paga no cronograma já diz que ela foi paga (#6342).
                                                        entradaPagaNoCronograma={acordo.parcelamento_parcelas.some(p => (p as { tipo?: string }).tipo === 'entrada' && p.status === 'pago')}
                                                        expiraEm={acordo.expira_em}
                                                        dividaBruta={acordo.divida_bruta}
                                                        outrosDebitos={acordo.memoria_calculo?.detalheOutrosDebitos}
                                                        descontoDivida={acordo.desconto_divida}
                                                        descontoOrigem={acordo.desconto_origem}
                                                        descontoDescricao={acordo.desconto_descricao}
                                                        jurosTotal={acordo.juros_total}
                                                        taxaJurosMensal={acordo.taxa_juros_mensal}
                                                        saldoKwh={acordo.saldo_kwh}
                                                        cancelamentoMotivo={acordo.cancelamento_motivo}
                                                    />
                                                </div>

                                                {['ativo', 'em_atraso', 'aguardando_aprovacao', 'aguardando_assinatura', 'aguardando_entrada'].includes(acordo.status) ? (
                                                    <div className="pt-4 border-t border-gray-100 dark:border-gray-800 space-y-2">
                                                        {acordo.status === 'aguardando_aprovacao' && podeAprovar ? (
                                                            <button
                                                                onClick={() => abrirDialogo({ tipo: 'aprovar', acordoId: acordo.id })}
                                                                disabled={cancelingId === acordo.id}
                                                                className="w-full px-3 py-2 bg-amber-50 hover:bg-amber-500 text-amber-700 hover:text-white font-bold text-xs rounded-lg transition-all flex items-center justify-center gap-2 border border-amber-200 hover:border-amber-500 active:scale-[0.98] disabled:opacity-50"
                                                            >
                                                                <CheckCircle2 size={14} /> Aprovar proposta
                                                            </button>
                                                        ) : null}

                                                        {acordo.status === 'aguardando_assinatura' && !acordo.termo_id_autentique ? (
                                                            <button
                                                                onClick={() => abrirDialogo({ tipo: 'termo', acordo })}
                                                                disabled={cancelingId === acordo.id}
                                                                className="w-full px-3 py-2 bg-primary/10 hover:bg-primary text-primary hover:text-white font-bold text-xs rounded-lg transition-all flex items-center justify-center gap-2 border border-primary/30 hover:border-primary active:scale-[0.98] disabled:opacity-50"
                                                            >
                                                                <Send size={14} /> Enviar termo para assinatura
                                                            </button>
                                                        ) : null}

                                                        {/* Efetivar na mão só faz sentido enquanto o acordo está
                                                            pendente — depois de vigente não há o que efetivar. */}
                                                        {['aguardando_assinatura', 'aguardando_entrada'].includes(acordo.status) ? (
                                                            <button
                                                                onClick={() => abrirDialogo({ tipo: 'efetivar', acordoId: acordo.id })}
                                                                disabled={cancelingId === acordo.id}
                                                                className="w-full px-3 py-2 bg-emerald-50 hover:bg-emerald-600 text-emerald-700 hover:text-white font-bold text-xs rounded-lg transition-all flex items-center justify-center gap-2 border border-emerald-200 hover:border-emerald-600 active:scale-[0.98] disabled:opacity-50"
                                                            >
                                                                {cancelingId === acordo.id
                                                                    ? <><Loader2 className="animate-spin" size={14} /> Processando…</>
                                                                    : <><CheckCircle2 size={14} /> Efetivar agora</>}
                                                            </button>
                                                        ) : null}

                                                        {/* Mesma regra da edge function: sem ela, o analista
                                                            escrevia o motivo e só então ouvia "sem permissão". */}
                                                        {podeCancelarAcordo(profile?.role, acordo) ? (
                                                            <button
                                                                onClick={() => abrirDialogo({ tipo: 'cancelar', acordoId: acordo.id })}
                                                                disabled={cancelingId === acordo.id}
                                                                className="w-full px-3 py-2 bg-red-50 hover:bg-red-600 text-red-600 hover:text-white font-bold text-xs rounded-lg transition-all flex items-center justify-center gap-2 border border-red-200 hover:border-red-600 active:scale-[0.98] disabled:opacity-50"
                                                            >
                                                                {cancelingId === acordo.id
                                                                    ? <><Loader2 className="animate-spin" size={14} /> Processando…</>
                                                                    : <><XCircle size={14} /> Cancelar acordo</>}
                                                            </button>
                                                        ) : (
                                                            <p className="text-[11px] text-center text-gray-400 dark:text-slate-500">
                                                                {profile?.role === 'analista'
                                                                    ? 'Este acordo já está valendo: cancelar é com gerente ou administrador.'
                                                                    : 'Cancelar este acordo é com gerente ou administrador.'}
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : null}
                                            </div>

                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            </>
            )}

            {/* Modals */}
            <CriarParcelamentoModal
                isOpen={isCreateModalOpen}
                onClose={() => { setIsCreateModalOpen(false); setFaturasPreSelecionadas(undefined); }}
                onSuccess={fetchAcordos}
                preSelectedFaturas={faturasPreSelecionadas}
            />

            <ConfirmModal
                isOpen={!!dialogo}
                onClose={fecharDialogo}
                onConfirm={confirmarDialogo}
                confirmDisabled={confirmarDesabilitado}
                variant={dialogo?.tipo === 'cancelar' ? 'danger' : dialogo?.tipo === 'efetivar' ? 'warning' : 'info'}
                title={
                    dialogo?.tipo === 'cancelar' ? 'Cancelar acordo'
                        : dialogo?.tipo === 'efetivar' ? 'Efetivar acordo agora'
                            : dialogo?.tipo === 'aprovar' ? 'Aprovar proposta fora da alçada'
                                : 'Enviar termo para assinatura'
                }
                message={
                    dialogo?.tipo === 'cancelar'
                        ? 'As faturas originais voltam ao estado anterior e as cobranças do acordo que ainda não foram pagas são canceladas. O motivo fica registrado na trilha do acordo, junto com o seu nome.'
                        : dialogo?.tipo === 'efetivar'
                            ? 'Efetivar sem esperar a conferência automática marca as faturas originais como "Parcelado" e gera as cobranças das parcelas. Explique por que está efetivando na mão — fica na trilha, com o seu nome.'
                            : dialogo?.tipo === 'aprovar'
                                ? 'Seu nome fica registrado na trilha do acordo, e a validade da proposta passa a contar de hoje.'
                                : dialogo?.tipo === 'termo' && dialogo.acordo.termo_dados?.coobrigado
                                    ? 'O termo vai para o titular e o responsável pela dívida assinarem, e também para o procurador e as 2 testemunhas configurados em Configurações. O acordo só vale depois das duas assinaturas.'
                                    : 'O termo vai para o cliente assinar, e também para o procurador e as 2 testemunhas configurados em Configurações.'
                }
                confirmLabel={
                    dialogo?.tipo === 'cancelar' ? 'Cancelar acordo'
                        : dialogo?.tipo === 'efetivar' ? 'Efetivar agora'
                            : dialogo?.tipo === 'aprovar' ? 'Aprovar proposta'
                                : 'Enviar termo'
                }
                cancelLabel={dialogo?.tipo === 'cancelar' ? 'Voltar' : 'Cancelar'}
            >
                {pedeMotivo ? (
                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
                            Motivo *
                        </label>
                        <textarea
                            value={motivoDialogo}
                            onChange={e => setMotivoDialogo(e.target.value)}
                            rows={3}
                            autoFocus
                            placeholder={dialogo?.tipo === 'cancelar' ? 'Por que este acordo está sendo cancelado?' : 'Por que está efetivando manualmente?'}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100 resize-y"
                        />
                        <p className={`text-[11px] mt-1 ${motivoDialogo.trim().length < MOTIVO_MINIMO ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-slate-500'}`}>
                            {motivoDialogo.trim().length < MOTIVO_MINIMO
                                ? `Faltam ${MOTIVO_MINIMO - motivoDialogo.trim().length} caractere(s) — o motivo precisa de pelo menos ${MOTIVO_MINIMO}.`
                                : 'Fica na trilha do acordo, com o seu nome.'}
                        </p>
                    </div>
                ) : dialogo?.tipo === 'termo' ? (
                    <div className="space-y-3">
                        {dialogo.acordo.termo_dados?.devedor.nome ? (
                            <p className="text-xs text-gray-500 dark:text-slate-400">
                                Titular: <strong className="text-gray-700 dark:text-slate-200">{dialogo.acordo.termo_dados.devedor.nome}</strong>
                            </p>
                        ) : null}
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
                                    E-mail do titular *
                                </label>
                                <input
                                    type="email"
                                    value={emailTermo}
                                    onChange={e => setEmailTermo(e.target.value)}
                                    autoFocus
                                    placeholder="cliente@email.com"
                                    className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
                                    WhatsApp
                                </label>
                                <input
                                    type="text"
                                    value={whatsappTermo}
                                    onChange={e => setWhatsappTermo(e.target.value)}
                                    placeholder="(64) 99999-9999"
                                    className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                        </div>
                        {dialogo.acordo.termo_dados?.coobrigado ? (
                            <div className="pt-3 border-t border-gray-100 dark:border-slate-700 space-y-2">
                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                    Responsável pela dívida: <strong className="text-gray-700 dark:text-slate-200">{dialogo.acordo.termo_dados.coobrigado.nome}</strong>
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
                                            E-mail do responsável *
                                        </label>
                                        <input
                                            type="email"
                                            value={emailCoobTermo}
                                            onChange={e => setEmailCoobTermo(e.target.value)}
                                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
                                            WhatsApp
                                        </label>
                                        <input
                                            type="text"
                                            value={whatsappCoobTermo}
                                            onChange={e => setWhatsappCoobTermo(e.target.value)}
                                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100"
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </ConfirmModal>

            {telasFaturaParcela}

            <FaturaFAB onAdd={() => {}} onRefresh={fetchAcordos} />
        </div>
    );
};

export default ParcelamentosView;
